import type { CalcAst } from './ast'
import type { CalcResultType } from '../visualSpec'

export interface TypecheckResult {
  result_type: CalcResultType
  is_aggregate: boolean
  is_table_calc: boolean
  has_row_level_outside_agg: boolean
}

const AGG_FUNCS = new Set(['sum', 'avg', 'min', 'max', 'count', 'countd', 'attr', 'agg'])
const TABLE_CALC_FUNCS = new Set([
  'running_sum',
  'running_avg',
  'running_count',
  'running_min',
  'running_max',
  'window_sum',
  'window_avg',
  'window_count',
  'window_min',
  'window_max',
  'index',
  'size',
  'total',
  'rank',
  'rank_dense',
  'lookup',
  'previous_value',
  'first',
  'last',
])

const STRING_FUNCS = new Set([
  'str',
  'lower',
  'upper',
  'trim',
  'ltrim',
  'rtrim',
  'left',
  'right',
  'mid',
  'char',
  'replace',
  'datename',
])

const NUMBER_FUNCS = new Set([
  'int',
  'float',
  'abs',
  'round',
  'ceiling',
  'ceil',
  'floor',
  'power',
  'sqrt',
  'len',
  'length',
  'ascii',
  'datediff',
  'datepart',
  'find',
  'findnth',
  // Tableau date-part convenience functions
  'year',
  'quarter',
  'month',
  'week',
  'day',
  'hour',
  'minute',
  'second',
  'weekday',
])

const BOOL_FUNCS = new Set(['isnull', 'contains', 'startswith', 'endswith'])

const DATE_FUNCS = new Set(['date', 'today', 'makedate'])
const TIMESTAMP_FUNCS = new Set(['datetime', 'now', 'dateadd', 'datetrunc'])

export function typecheckCalcAst(ast: CalcAst | null): TypecheckResult {
  if (!ast) return { result_type: 'unknown', is_aggregate: false, is_table_calc: false, has_row_level_outside_agg: false }

  let isAgg = false
  let isTable = false
  let hasRowLevelOutsideAgg = false

  const visit = (n: CalcAst, inAggContext: boolean) => {
    if (n.kind === 'call') {
      const name = String(n.name || '').trim().toLowerCase()
      const isAggFn = AGG_FUNCS.has(name)
      const isTableFn = TABLE_CALC_FUNCS.has(name)
      if (isAggFn) isAgg = true
      if (isTableFn) isTable = true
      const nextAgg = inAggContext || isAggFn || isTableFn
      for (const a of n.args) visit(a, nextAgg)
    } else if (n.kind === 'lod') {
      // LOD expressions are always aggregates
      isAgg = true
      // Visit dimensions and expression
      for (const dim of n.dimensions) visit(dim, true)
      visit(n.expression, true)
    } else if (n.kind === 'field_ref') {
      if (!inAggContext) hasRowLevelOutsideAgg = true
    } else if (n.kind === 'binary') {
      visit(n.left, inAggContext)
      visit(n.right, inAggContext)
    } else if (n.kind === 'unary') {
      visit(n.expr, inAggContext)
    } else if (n.kind === 'if') {
      for (const b of n.branches) {
        visit(b.when, inAggContext)
        visit(b.then, inAggContext)
      }
      if (n.else_expr) visit(n.else_expr, inAggContext)
    } else if (n.kind === 'case') {
      if (n.base) visit(n.base, inAggContext)
      for (const w of n.whens) {
        visit(w.when, inAggContext)
        visit(w.then, inAggContext)
      }
      if (n.else_expr) visit(n.else_expr, inAggContext)
    }
  }

  visit(ast, false)

  // v1 heuristic: if any comparisons/boolean ops exist, treat as bool; otherwise unknown.
  const inferType = (n: CalcAst): CalcResultType => {
    if (n.kind === 'literal') {
      if (n.literal_kind === 'bool') return 'bool'
      if (n.literal_kind === 'string') return 'string'
      if (n.literal_kind === 'number') return 'number'
      return 'unknown'
    }
    if (n.kind === 'binary') {
      const isDateish = (t: CalcResultType): boolean => t === 'date' || t === 'timestamp'
      if (['=', '!=', '<>', '<', '<=', '>', '>=', 'and', 'or', 'in'].includes(n.op)) return 'bool'
      if (n.op === '+') {
        const lt = inferType(n.left)
        const rt = inferType(n.right)
        if (lt === 'string' || rt === 'string') return 'string'
        if (lt === 'number' && rt === 'number') return 'number'
        if ((isDateish(lt) && rt === 'number') || (lt === 'number' && isDateish(rt))) return 'timestamp'
        return 'unknown'
      }
      if (n.op === '-') {
        const lt = inferType(n.left)
        const rt = inferType(n.right)
        if (isDateish(lt) && rt === 'number') return 'timestamp'
        if (isDateish(lt) && isDateish(rt)) return 'number'
        return 'number'
      }
      if (n.op === '*' || n.op === '/' || n.op === '^') return 'number'
      return 'unknown'
    }
    if (n.kind === 'unary' && n.op === 'not') return 'bool'
    if (n.kind === 'call') {
      const name = String(n.name || '').toLowerCase()
      if (BOOL_FUNCS.has(name)) return 'bool'
      if (name === 'zn') return 'number'
      if (name === 'iif') {
        const t = (n.args || [])[1] ? inferType((n.args || [])[1]) : 'unknown'
        const e = (n.args || [])[2] ? inferType((n.args || [])[2]) : 'unknown'
        if (t === e) return t
        if (t === 'unknown') return e
        if (e === 'unknown') return t
        return 'unknown'
      }
      if (name === 'agg') {
        const a0 = (n.args || [])[0]
        return a0 ? inferType(a0) : 'unknown'
      }
      if (name === 'attr') {
        const a0 = (n.args || [])[0]
        return a0 ? inferType(a0) : 'unknown'
      }
      if (name === 'min' || name === 'max' || name === 'window_min' || name === 'window_max' || name === 'lookup' || name === 'previous_value' || name === 'running_min' || name === 'running_max') {
        const a0 = (n.args || [])[0]
        return a0 ? inferType(a0) : 'unknown'
      }
      if (STRING_FUNCS.has(name)) return 'string'
      if (NUMBER_FUNCS.has(name) || AGG_FUNCS.has(name) || TABLE_CALC_FUNCS.has(name)) return 'number'
      if (DATE_FUNCS.has(name)) return 'date'
      if (TIMESTAMP_FUNCS.has(name)) return 'timestamp'
      return 'unknown'
    }
    if (n.kind === 'lod') {
      // LOD expression type is determined by the inner expression
      return inferType(n.expression)
    }
    return 'unknown'
  }

  return { result_type: inferType(ast), is_aggregate: isAgg, is_table_calc: isTable, has_row_level_outside_agg: hasRowLevelOutsideAgg }
}
